import { Link as InertiaLink, usePage } from '@inertiajs/react';
import PropTypes from 'prop-types';

import { alpha, styled } from '@mui/material/styles';

import Divider from '@mui/material/Divider';
import List from '@mui/material/List';

import { Box, Tooltip } from '@mui/material';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import BuildIcon from '@mui/icons-material/Build';
import GridView from '@mui/icons-material/GridView';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import PolicyIcon from '@mui/icons-material/Policy';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StraightenIcon from '@mui/icons-material/Straighten';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

import { PageProps } from '@/types';
import { AdminPanelSettings } from '@mui/icons-material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Calibration from '../svgIcons/Calibration';
import Database from '../svgIcons/Database';
import File from '../svgIcons/File';
import FolderOpen from '../svgIcons/FolderOpen';
import Status from '../svgIcons/Status';

export const DrawerItem = styled(ListItem)(({ theme }) => {
  return {
    display: 'block',
    color: '#9499A6',
    '& .MuiListItemButton-root': {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      borderRadius: 0,
      position: 'relative',
      color: theme.palette.primary.contrastText,
      '&:after': {
        content: '" "',
        display: 'block',
        width: 4,
        background: 'transparent',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        transition: 'background .2s',
        clipPath: 'polygon(0% 0%, 100% 10%, 100% 90%, 0% 100%)',
      },
      '&:hover': {
        background: alpha(theme.palette.grey[200], 0.05),
        '&:after': {
          background: theme.palette.grey[200],
        },
      },
      '&.Mui-selected': {
        background: alpha(theme.palette.secondary.main, 0.05),
        color: theme.palette.secondary.main,
        '&:hover': {
          '&:after': {
            background: theme.palette.secondary.main,
          },
        },
        '&:after': {
          background: theme.palette.secondary.main,
        },
        // boxShadow: '0 15px 30px #0001',
      },
    },
    '& .MuiSvgIcon-root': {
      fontSize: '1.25rem',
    },
  };
});

function DrawerContent({
  open,
  onElementClick,
  onClose,
}: {
  open: boolean;
  onElementClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  onClose: () => void;
}) {
  const { routes, auth } = usePage<PageProps>().props;

  const {
    user: { permissions, company },
  } = auth;

  const handleElementClick = (e) => {
    onElementClick?.(e);
  };

  // Helper to check if a module is visible at company level
  const isModuleVisible = (moduleKey: string): boolean => {
    const visibility = company?.module_visibility;
    if (!visibility) return true; // Default to visible if not set
    return visibility[moduleKey] !== false;
  };

  const items = [
    {
      id: 'chatbot',
      to: '/app/chatbot',
      label: 'FactoryQA AI Assistant',
      icon: () => <SmartToyIcon />,
      show: !!company?.chatbot_enabled && isModuleVisible('chatbot'),
      selected: routes.path.includes('chatbot'),
    },
    {
      id: 'dashboard',
      to: '/app/dashboard',
      label: 'Dashboard',
      icon: () => <GridView />,
      show: isModuleVisible('dashboard'),
      selected:
        routes.path.includes('dashboard') ||
        window.location.pathname === '/app',
    },
    {
      id: 'compliance',
      to: route('compliance.dashboard'),
      label: 'Compliance',
      icon: () => <PolicyIcon />,
      show: !!company?.ai_auditor_enabled && isModuleVisible('compliance'),
      selected: routes.path.startsWith('app/compliance'),
    },
    {
      id: 'quality-plan',
      to: '/app/quality-plan',
      label: 'Quality Plans',
      icon: () => <StraightenIcon />,
      show:
        permissions.quality_plans !== 'none' &&
        isModuleVisible('quality_plans'),
      selected: routes.path.startsWith('app/quality-plan'),
    },
    {
      id: 'document-control',
      to: '/app/document-control',
      label: 'Documents',
      icon: () => <FolderOpen />,
      show:
        permissions.document_control !== 'none' &&
        isModuleVisible('document_control'),
      selected: routes.path.includes('document-control'),
      //disabled: auth.subscriptions.document_control.type === 'none',
    },
    {
      id: 'action-items',
      to: '/app/action-items',
      label: 'Tasks',
      icon: () => <Status />,
      show:
        permissions.action_items !== 'none' && isModuleVisible('action_items'),
      selected: routes.path.includes('action-items'),
    },
    {
      id: 'downtimes',
      to: '/app/downtimes',
      label: 'Issues',
      icon: () => <Database />,
      show:
        permissions.issue_tracker !== 'none' &&
        isModuleVisible('issue_tracker'),
      selected:
        routes.path.includes('downtimes') ||
        routes.path.startsWith('app/issue-tracker'),
    },
    {
      id: 'inspection-sheets',
      to: '/app/inspection-sheets/user',
      label: 'Inspection',
      icon: () => <File />,
      show:
        permissions.inspection_sheets !== 'none' &&
        isModuleVisible('inspection_sheets'),
      selected: routes.path.startsWith('app/inspection-sheets'),
      //disabled: auth.subscriptions.inspection_sheet.type === 'none',
    },
    {
      id: 'calibration',
      to: route('gages.index'),
      label: company?.inventory_mode ? 'Inventory' : 'Calibration',
      icon: () => <Calibration />,
      show:
        permissions.calibration !== 'none' && isModuleVisible('calibration'),
      selected:
        routes.path.includes('calibration') &&
        !routes.path.includes('dashboard'),
      //disabled: auth.subscriptions.calibration_management.type === 'none',
    },
    {
      id: 'preventive-maintenance',
      to: route('preventive-maintenance.index'),
      label: 'Preventive Maintenance',
      icon: () => <BuildIcon />,
      show:
        permissions.preventive_maintenance !== 'none' &&
        isModuleVisible('preventive_maintenance'),
      selected: routes.path.includes('preventive-maintenance'),
    },
    {
      id: 'supplier-management',
      to: route('supplier-management.current.index'),
      label: 'Supplier Quality',
      icon: () => <AdminPanelSettings />,
      show:
        permissions.supplier_settings !== 'none' &&
        isModuleVisible('supplier_management'),
      selected: routes.path.includes('supplier-management'),
    },
    {
      id: 'audits',
      to: '/app/audits',
      label: 'Audits',
      icon: () => <VerifiedUserIcon />,
      show: isModuleVisible('audits'),
      selected: routes.path.includes('audits'),
      //disabled: auth.subscriptions.audits.type === 'none',
    },
    {
      id: 'lms',
      to: '/app/lms/courses',
      label: 'LMS',
      icon: () => <OndemandVideoIcon />,
      show: isModuleVisible('lms'),
      selected: routes.path.includes('lms'),
      //disabled: auth.subscriptions.audits.type === 'none',
    },
  ];

  return (
    <>
      <List
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          paddingX: open ? 2 : 0,
        }}
      >
        {items?.map((item) => {
          if (item.type && item.type === 'divider') {
            return <Divider key={item.id} />;
          }

          if (item.type && item.type === 'spacer') {
            return <Box key={item.id} sx={{ flexGrow: 1 }} />;
          }
          const Icon = item.icon || null;

          return item.show ? (
            <Tooltip
              disableFocusListener={open}
              disableHoverListener={open}
              disableTouchListener={open}
              title={item.label}
              key={item.id}
              placement="right"
              arrow
            >
              <DrawerItem disablePadding>
                <ListItemButton
                  onClick={(e) => {
                    item.onClick?.(e);
                    handleElementClick(e);
                  }}
                  component={InertiaLink}
                  href={item.to}
                  prefetch="hover"
                  selected={item.selected}
                  disabled={item.disabled}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                    borderRadius: open ? 1 : 0,
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                      color: 'inherit',
                    }}
                  >
                    <Icon />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </DrawerItem>
            </Tooltip>
          ) : null;
        })}
      </List>
      <List
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingX: open ? 2 : 0,
        }}
      >
        <DrawerItem disablePadding>
          <ListItemButton
            onClick={onClose}
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              borderRadius: open ? 1 : 0,
              px: 2.5,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
                color: 'inherit',
              }}
            >
              {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </ListItemIcon>
            <ListItemText primary="Collapse" sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </DrawerItem>
      </List>
    </>
  );
}

DrawerContent.propTypes = {
  open: PropTypes.bool,
  onElementClick: PropTypes.func,
};

export default DrawerContent;
